import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Section, MiniStat, EmptyState, chartTooltip } from "./Section";
import { DEFINITIONS, fmtPct, fmtNum } from "./definitions";

const TopicTable = ({ rows, testId, rateKey = "ai_resolution_rate", rateLabel = "AI resolution" }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead>
      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
        <th className="py-2 font-medium">Topic</th><th className="py-2 font-medium text-right">Volume</th><th className="py-2 font-medium text-right">{rateLabel}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.topic} className="border-b border-slate-50">
          <td className="py-1.5 text-slate-700">{r.topic}</td>
          <td className="py-1.5 text-right text-slate-600">{fmtNum(rateKey === "ai_resolution_rate" ? r.count : r.ai_handled)}</td>
          <td className="py-1.5 text-right">
            <span className="inline-flex items-center gap-2">
              <span className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${r[rateKey] || 0}%` }} /></span>
              <span className="w-12 text-slate-700">{fmtPct(r[rateKey])}</span>
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const shortUrl = (u) => {
  try { const x = new URL(u); return `${x.hostname}${x.pathname}`.replace(/\/$/, ""); } catch { return u; }
};

export const KnowledgeSection = ({ knowledge }) => (
  <Section title="Knowledge gaps & improvement" testId="knowledge-section"
    info={["Topics are AI-tagged once per conversation and saved.", DEFINITIONS.fallback]}
    subtitle="Where the bot struggles and which articles it leans on">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
      <MiniStat label="Fallback rate" value={fmtPct(knowledge.fallback_rate)} hint={`${fmtNum(knowledge.fallback_messages)} of ${fmtNum(knowledge.bot_messages)} bot replies`} testId="stat-fallback-rate" />
      <MiniStat label="Top escalation reason" value={knowledge.escalation_reasons[0]?.reason || "—"} hint={knowledge.escalation_reasons[0] ? `${knowledge.escalation_reasons[0].count} handoffs` : "no handoffs"} testId="stat-top-reason" />
      <MiniStat label="Weakest topic" value={knowledge.weakest_topics[0]?.topic || "—"} hint={knowledge.weakest_topics[0] ? `${fmtPct(knowledge.weakest_topics[0].ai_handled_resolution_rate)} resolved by AI` : ""} testId="stat-weakest-topic" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Top 10 topics</p>
        {knowledge.top_topics.length ? <TopicTable rows={knowledge.top_topics} testId="top-topics-table" /> : <EmptyState message="No conversations yet." testId="topics-empty" />}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Top escalation reasons</p>
        {knowledge.escalation_reasons.length === 0 ? <EmptyState message="No handoffs in this period." testId="reasons-empty" /> : (
          <div className="h-[220px]" data-testid="escalation-reasons-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={knowledge.escalation_reasons} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="reason" width={150} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={chartTooltip} />
                <Bar dataKey="count" name="Handoffs" fill="#d97706" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1">Lowest AI resolution — write KB articles here</p>
        <p className="text-xs text-slate-500 mb-2">Among conversations the AI answered first (min. 2).</p>
        {knowledge.weakest_topics.length ? <TopicTable rows={knowledge.weakest_topics} testId="weakest-topics-table" rateKey="ai_handled_resolution_rate" rateLabel="Resolved by AI" />
          : <EmptyState message="Not enough AI-handled conversations per topic yet." testId="weakest-empty" />}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Most-cited KB articles</p>
        {knowledge.kb_articles.length === 0 ? <EmptyState message="The bot hasn't linked any Knowledge Base articles in this period." testId="kb-cited-empty" /> : (
          <ul className="space-y-1.5 text-sm" data-testid="kb-cited-list">
            {knowledge.kb_articles.map((a) => (
              <li key={a.url} className="flex justify-between gap-3">
                <a href={a.url} target="_blank" rel="noreferrer" className="text-[#425B76] hover:underline truncate">{shortUrl(a.url)}</a>
                <span className="text-slate-500 shrink-0">{a.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </Section>
);
